import { useTranslation } from "react-i18next";
import { useContext, useState } from "react";
import { useAppMarket } from "../../../services/useAppMarket";

import {
  WellHouseSmall,
  WellHouseTitleWrapper,
  EnableMarketStepOneWrapper,
  StepOneContainer,
  WellHouseIcon,
  Title,
  Paragraph,
  BoxContainer,
  BoxTitle,
  BoxSubtitle,
  ConnectBtn,
  TechnicalSupportWrapper,
  SupportTitle,
  SupportSubtitle,
  BtnsContainer,
  ContactUs,
  InstallationGuid,
  StyledLeftArrow,
  EnableMarketStepTwoWrapper,
  StepTwoContainer,
  StepTwoTitle,
  InputContainer,
  ApiInput,
  InputLabel,
  HelperText,
  LinkText,
  HighlightedLink,
  SaveButton,
  SupportSection,
  Error,
  FormElement,
} from "../styles/EnableMarketPlace";
import { ToastMessageContext } from "@context/toast-message-cotnext";

import { useForm, Controller, FieldError } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { CircularProgress } from "@mui/material";

// Define interfaces for the form field structure
interface FormRule {
  required?: boolean;
  maxlength?: number;
  regex?: string;
}

interface FormField {
  label: string;
  type: string;
  name: string;
  palceHolderEn: string;
  palceHolderAr: string;
  fullWidth: boolean;
  defaultValue: string;
  rules: FormRule[];
}

interface FormData {
  [key: string]: string;
}

const EnableMarketPlace: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [enableMarketStep, setEnableMarketStep] = useState<number>(1);
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const isRtl = language === "ar";

  const { showError, showSuccess } = useContext(ToastMessageContext);

  // Queries & mutations
  const { ActivateAppMutate, GetMarketPlaceByIdQuery } = useAppMarket();
  const { data: app, isLoading } = GetMarketPlaceByIdQuery({ id: id });
  const { mutate: updateApp, isPending } = ActivateAppMutate({
    onError: (error: any) => {},
    onSuccess: () => navigate("/appLinkedSuccess" + `/${id}`),
  });

  // Initialize form with default values
  const defaultValues: FormData = {};
  const apiDocument = app?.result?.uRls?.apiDocumentUrl;
  const customerService = app?.result?.uRls?.appSupportUrl;
  const register = app?.result?.uRls?.registerUrl;
  if (app?.result?.form && Array.isArray(app?.result?.form)) {
    app?.result?.form?.forEach((field: FormField) => {
      defaultValues[field?.name] = field?.defaultValue || "";
    });
  }

  const { control, handleSubmit } = useForm<FormData>({
    defaultValues,
  });

  const onSubmit = (data: FormData) => {
    updateApp({ id: id, body: data });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {/* header */}
      <WellHouseTitleWrapper>
        <WellHouseSmall />
        {t("AppMarket.WellHouseTitle")}
      </WellHouseTitleWrapper>

      {/* step 1 section  */}
      <EnableMarketStepOneWrapper
        enableMarketStep={enableMarketStep}
        $isRtl={isRtl}
      >
        <StepOneContainer>
          <WellHouseIcon />
          <Title>{t("AppMarket.welcome")}</Title>
          <Paragraph>{t("AppMarket.wheelhouseDescription")}</Paragraph>
          <BoxContainer>
            <BoxTitle>{t("AppMarket.connectWheelhouseAccount")}</BoxTitle>
            <BoxSubtitle>
              {t("AppMarket.wheelhouseAccountDescription")}
            </BoxSubtitle>
            <ConnectBtn onClick={() => setEnableMarketStep(2)}>
              {t("AppMarket.connectWheelhouseAccount")}
            </ConnectBtn>
          </BoxContainer>
        </StepOneContainer>
      </EnableMarketStepOneWrapper>

      {/* technical support for wellhouse */}
      <SupportSection enableMarketStep={enableMarketStep} $isRtl={isRtl}>
        <TechnicalSupportWrapper>
          <SupportTitle>{t("AppMarket.wheelhouseSupport")}</SupportTitle>
          <SupportSubtitle>
            {t("AppMarket.wheelhouseSupportDescription")}
          </SupportSubtitle>
          <BtnsContainer>
            <ContactUs onClick={() => window.open(customerService, "_blank")}>
              {t("AppMarket.contactSupport")}
            </ContactUs>
            <InstallationGuid
              onClick={() => window.open(apiDocument, "_blank")}
              $isRtl={isRtl}
            >
              {t("AppMarket.wheelhouseInstallationGuide")}
              <StyledLeftArrow $isRtl={isRtl} />
            </InstallationGuid>
          </BtnsContainer>
        </TechnicalSupportWrapper>
      </SupportSection>

      {/* step 2 section  */}
      <EnableMarketStepTwoWrapper
        enableMarketStep={enableMarketStep}
        $isRtl={isRtl}
      >
        <StepTwoContainer>
          <WellHouseIcon />

          {app?.result?.form?.map((formField: FormField) => {
            // Get placeholder based on language
            const fieldPlaceholder = language === 'ar'
              ? formField?.palceHolderAr
              : formField?.palceHolderEn;
            const fieldLabel = formField?.label;
            return (
              <FormElement>
                <StepTwoTitle>{fieldLabel}</StepTwoTitle>
                <InputContainer key={formField.name}>
                  <Controller
                    name={formField.name as any}
                    control={control}
                    rules={{
                      required: formField?.rules?.some(
                        (rule) => rule?.required
                      ),
                      maxLength: formField?.rules?.find(
                        (rule) => rule?.maxlength
                      )?.maxlength,
                      pattern: formField?.rules?.find((rule) => rule?.regex)
                        ? new RegExp(
                            formField?.rules?.find((rule) => rule?.regex)
                              ?.regex || ""
                          )
                        : undefined,
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <ApiInput
                          placeholder={fieldPlaceholder}
                          type={formField.type}
                          {...field}
                        />
                        {error && (
                          <Error $isRtl={isRtl}>
                            {error.type === "required"
                              ? t("AppMarket.thisFieldRequired")
                              : error.type === "maxLength"
                                ? `Maximum length is ${formField.rules.find((r) => r.maxlength)?.maxlength} characters`
                                : error.type === "pattern"
                                  ? t("AppMarket.invalidFormat")
                                  : t("AppMarket.invalidInput")}
                          </Error>
                        )}
                      </>
                    )}
                  />
                  <InputLabel>{formField.label}</InputLabel>
                </InputContainer>
              </FormElement>
            );
          })}

          <HelperText>{t("AppMarket.getApiCredentialsFromWihouse")}</HelperText>
          <LinkText>
            {t("AppMarket.requiredAccount")}
            <HighlightedLink onClick={() => window.open(register, "_blank")}>
              {t("AppMarket.fromHere")}
            </HighlightedLink>
          </LinkText>
          <SaveButton type="submit" disabled={isPending}>
            {isPending ? (
              <CircularProgress color="inherit" size={20} />
            ) : (
              t("AppMarket.save")
            )}
          </SaveButton>
        </StepTwoContainer>
      </EnableMarketStepTwoWrapper>
    </form>
  );
};

export default EnableMarketPlace;
